import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Play } from "lucide-react";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import { api } from "@/lib/api";
import { PosterTile } from "@/components/Studio";
import NotFound from "../NotFound";

export default function SeriesPage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    api.get(`/public/series/${slug}`).then((r) => setData(r.data)).catch(() => setMissing(true));
  }, [slug]);

  if (missing) return <NotFound />;
  if (!data) {
    return <div className="bg-ink min-h-screen pt-40 px-6"><p className="font-ui text-paper/40 text-xs uppercase tracking-[0.3em] text-center">Loading series…</p></div>;
  }
  const { series, episodes } = data;
  const seasons = {};
  for (const e of episodes) {
    const s = e.season || 1;
    seasons[s] = seasons[s] || [];
    seasons[s].push(e);
  }

  return (
    <div className="bg-ink min-h-screen" data-testid="series-page">
      <Seo title={`${series.title} — Original Series — Dirty Church Chronicles Studios`}
        description={series.logline || series.synopsis || "An original series from Dirty Church Chronicles Studios."}
        path={`/cinema/series/${series.slug}`} />
      <section className="pt-36 pb-14 max-w-7xl mx-auto px-6 sm:px-10">
        <Link to="/cinema/original-series" data-testid="series-back-link"
          className="inline-flex items-center gap-2 font-ui text-xs uppercase tracking-[0.22em] text-paper/50 hover:text-paper transition-colors mb-8">
          <ArrowLeft size={13} /> Original Series
        </Link>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
          <Reveal className="lg:col-span-4">
            <div className="aspect-[2/3] max-w-xs border border-paper/15 overflow-hidden shadow-[0_30px_80px_-20px_rgba(0,0,0,0.9)]" data-testid="series-poster">
              <PosterTile video={series} />
            </div>
          </Reveal>
          <Reveal delay={0.12} className="lg:col-span-8">
            <p className="eyebrow mb-3">Original Series</p>
            <h1 className="font-display font-black uppercase text-paper text-4xl sm:text-6xl tracking-tight mb-4" data-testid="series-title">
              {series.title}
            </h1>
            {series.logline && <p className="font-reading italic text-xl text-gold/85 mb-5">{series.logline}</p>}
            {series.synopsis && <p className="font-reading text-xl text-paper/70 leading-relaxed max-w-2xl">{series.synopsis}</p>}
            <p className="font-ui text-xs text-paper/45 mt-5 uppercase tracking-[0.2em]">
              {Object.keys(seasons).length} season{Object.keys(seasons).length !== 1 ? "s" : ""} · {episodes.length} episode{episodes.length !== 1 ? "s" : ""}
            </p>
          </Reveal>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 sm:px-10 pb-28 space-y-14">
        {Object.entries(seasons).map(([season, eps]) => (
          <div key={season} data-testid={`season-${season}`}>
            <h2 className="font-display font-bold text-2xl text-paper mb-6">Season {season}</h2>
            <div className="divide-y divide-paper/10 border-y border-paper/10">
              {eps.map((e) => (
                <Link key={e.video_id} to={`/cinema/watch/${e.slug}`} data-testid={`episode-${e.slug}`}
                  className="group flex items-center gap-6 py-6 transition-colors duration-300">
                  <span className="font-display text-crimson text-xl w-10 shrink-0">{String(e.episode || "").padStart(2, "0")}</span>
                  <span className="w-24 shrink-0 aspect-video overflow-hidden border border-paper/10 relative">
                    <PosterTile video={e} />
                    <span className="absolute inset-0 flex items-center justify-center bg-ink/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <Play size={16} className="text-paper" />
                    </span>
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="block font-display text-lg text-paper group-hover:text-crimsonhi transition-colors truncate">{e.title}</span>
                    {e.logline && <span className="block font-ui text-xs text-paper/45 mt-1 truncate">{e.logline}</span>}
                  </span>
                  {e.runtime && <span className="font-ui text-xs text-paper/40 shrink-0">{e.runtime}</span>}
                </Link>
              ))}
            </div>
          </div>
        ))}
        {episodes.length === 0 && (
          <p className="font-ui text-sm text-paper/45 border border-paper/10 p-10">Episodes are in production. The premiere schedule will appear here.</p>
        )}
      </section>
    </div>
  );
}
